import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Button } from 'components/button/Button';
import { FieldArray } from 'formik';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { updateGeneralSettings } from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { useAllGeneralSettings } from '../all-settings-context';
import { ISimpleComboboxItem } from '../../../components/combobox/SimpleCombobox';
import { cargoTypeToComboboxItem, CargoTypeComboboxField } from '../../order/cargoType/CargoTypeComboboxField';
import { PackageTypeComboboxField, packageTypeToComboboxItem } from '../../order/packageType/PackageTypeComboboxField';
import { ProductTypeComboboxField, productTypeToComboboxItem } from '../../order/productType/ProductTypeComboboxField';
import { InputField } from '../../../components/input/InputField';
import { VatRateComboboxField, vatRateToComboboxItem } from '../../order/vatRate/VatRateComboboxField';
import { decimateAndFormatNumber, parseNumberInput } from '../../../utils/number';
import { captureException } from '@sentry/react';
import { useTranslation } from '../../../contexts/translation-context';
import { Card } from 'components/Card';

const orderSettingsSchema = Yup.object().shape({
  cargoType: Yup.mixed().nullable().required('Vereist'),
  packageType: Yup.mixed().nullable().required('Vereist'),
  productType: Yup.mixed().nullable().required('Vereist'),
  vatRate: Yup.mixed().nullable().required('Vereist'),
  orderNumber: Yup.string().required('Vereist'),
  emissionMultiplier: Yup.string().required('Vereist'),
});

interface IOrderSettingsValues {
  cargoType: ISimpleComboboxItem | null;
  packageType: ISimpleComboboxItem | null;
  productType: ISimpleComboboxItem | null;
  secondProductType: ISimpleComboboxItem | null;
  thirdProductType: ISimpleComboboxItem | null;
  vatRate: ISimpleComboboxItem | null;
  orderNumber: string;
  cmrReminderIntervals: string[];
  dieselSurchargeProductType: ISimpleComboboxItem | null;
  dieselSurchargeBaseProductType: ISimpleComboboxItem | null;
  emissionMultiplier: string;
}

const OrderSettingsPage = () => {
  const { settings } = useAllGeneralSettings();
  const { lang } = useTranslation();

  const initialValues: IOrderSettingsValues = useMemo(() => {
    return {
      cargoType: settings.defaultCargoType ? cargoTypeToComboboxItem(settings.defaultCargoType, lang) : null,
      packageType: settings.defaultPackageType ? packageTypeToComboboxItem(settings.defaultPackageType, lang) : null,
      productType: settings.defaultProductType ? productTypeToComboboxItem(settings.defaultProductType) : null,
      secondProductType: settings.secondDefaultProductType
        ? productTypeToComboboxItem(settings.secondDefaultProductType)
        : null,
      thirdProductType: settings.thirdDefaultProductType
        ? productTypeToComboboxItem(settings.thirdDefaultProductType)
        : null,
      vatRate: settings.defaultVatRate ? vatRateToComboboxItem(settings.defaultVatRate) : null,
      orderNumber: '' + settings.orderNumber,
      cmrReminderIntervals: settings.cmrReminderIntervals.map((v) => '' + v),
      dieselSurchargeProductType: settings.dieselSurchargeProductType
        ? productTypeToComboboxItem(settings.dieselSurchargeProductType)
        : null,
      dieselSurchargeBaseProductType: settings.dieselSurchargeBaseProductType
        ? productTypeToComboboxItem(settings.dieselSurchargeBaseProductType)
        : null,
      emissionMultiplier: decimateAndFormatNumber(Math.round(settings.emissionMultiplier * 1000), 3),
    };
  }, [settings, lang]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={orderSettingsSchema}
      onSubmit={async (values) => {
        try {
          const patch: Partial<typeof values> = diff(initialValues, values);
          const {
            cargoType,
            packageType,
            productType,
            secondProductType,
            thirdProductType,
            orderNumber,
            vatRate,
            cmrReminderIntervals,
            dieselSurchargeProductType,
            dieselSurchargeBaseProductType,
            emissionMultiplier,
            ...otherValues
          } = patch;
          await updateGeneralSettings({
            defaultCargoTypeId: cargoType?.key,
            defaultPackageTypeId: packageType?.key,
            defaultProductTypeId: productType?.key,
            secondDefaultProductTypeId: secondProductType?.key,
            thirdDefaultProductTypeId: thirdProductType?.key,
            defaultVatRateId: vatRate?.key,
            orderNumber: orderNumber ? +orderNumber : undefined,
            cmrReminderIntervals: cmrReminderIntervals ? cmrReminderIntervals.map((v) => +v) : undefined,
            emissionMultiplier: emissionMultiplier ? parseNumberInput(emissionMultiplier, 3) / 1000 : undefined,
            dieselSurchargeProductTypeId: dieselSurchargeProductType?.key,
            dieselSurchargeBaseProductTypeId: dieselSurchargeBaseProductType?.key,
            ...otherValues,
          });
          toast.success('Instellingen aangepast');
        } catch (err: any) {
          captureException(err);
          toast.error('Kon algemene instellingen niet aanpassen: ' + getDisplayError(err));
        }
      }}
    >
      {({ handleSubmit, isSubmitting, values }) => {
        return (
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="card p-5">
              <CargoTypeComboboxField labelText="Standaard inhoud" name="cargoType" />

              <PackageTypeComboboxField labelText="Standaard verpakking" name="packageType" />

              <ProductTypeComboboxField labelText="Standaard product type" name="productType" />

              <ProductTypeComboboxField labelText="Tweede standaard product type" name="secondProductType" />

              <ProductTypeComboboxField labelText="Derde standaard product type" name="thirdProductType" />

              <VatRateComboboxField labelText="Standaard btw type" name="vatRate" />

              <InputField labelText="Volgende order nummer" type="number" name="orderNumber" />
            </div>

            <Card title="Diesel toeslag">
              <ProductTypeComboboxField
                labelText="Diesel toeslag basis voor berekening"
                name="dieselSurchargeBaseProductType"
              />

              <ProductTypeComboboxField labelText="Diesel toeslag product" name="dieselSurchargeProductType" />

              <InputField labelText="Emissie multiplier" step="0.001" type="number" name="emissionMultiplier" />
            </Card>

            <FieldArray
              name="cmrReminderIntervals"
              render={(arrayHelpers) => (
                <Card
                  title="CMR herinneringen"
                  rightHeading={<Button onTrigger={() => arrayHelpers.push('')}>Voeg interval toe</Button>}
                >
                  {values.cmrReminderIntervals.map((interval, index) => (
                    <div className="flex items-center gap-2" key={index}>
                      <InputField
                        labelText={`Herinnering "${index + 1}" na x dagen`}
                        type="number"
                        name={`cmrReminderIntervals.${index}`}
                      />
                      <div>
                        <Button type="button" color="danger" onTrigger={() => arrayHelpers.remove(index)}>
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {values.cmrReminderIntervals.length === 0 && (
                    <div className="text-sm text-fg-muted">Nog geen herinneringen ingesteld.</div>
                  )}
                </Card>
              )}
            />

            <div>
              <Button
                type="submit"
                color="primary"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                iconLeft={<PlusIcon className="button-icon" />}
              >
                Pas instellingen aan
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default OrderSettingsPage;
